import { RefObject, useCallback, useEffect, useState } from 'react';

interface OverlayStyle {
  top: number;
  left: number;
  height: number;
}

interface UseActionOverlayPositionParams {
  containerRef: RefObject<HTMLDivElement>;
  actionCellRef: RefObject<HTMLTableCellElement>;
  fixedColumnWidth: string;
  enabled: boolean;
}

export const useActionOverlayPosition = ({
  containerRef,
  actionCellRef,
  fixedColumnWidth,
  enabled,
}: UseActionOverlayPositionParams) => {
  const [overlayStyle, setOverlayStyle] = useState<OverlayStyle | null>(null);

  const updatePosition = useCallback(() => {
    const container = containerRef.current;
    const actionCell = actionCellRef.current;

    if (!container || !actionCell || !enabled) return;

    const containerRect = container.getBoundingClientRect();
    const cellRect = actionCell.getBoundingClientRect();
    const viewportWidth = container.clientWidth;
    const labelWidth = parseFloat(fixedColumnWidth) || 88;

    const top = cellRect.top - containerRect.top;
    const height = cellRect.height;

    const availableWidth = viewportWidth - labelWidth;
    const contentWidth = 120;
    const centerLeft = labelWidth + (availableWidth - contentWidth) / 2;

    setOverlayStyle({ top, left: centerLeft, height });
  }, [containerRef, actionCellRef, fixedColumnWidth, enabled]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !enabled) return;

    const timer = setTimeout(updatePosition, 0);

    const resizeObserver = new ResizeObserver(updatePosition);
    resizeObserver.observe(container);

    return () => {
      clearTimeout(timer);
      resizeObserver.disconnect();
    };
  }, [containerRef, enabled, updatePosition]);

  return overlayStyle;
};
